import { ESIM_TYPE, SOFTSIM_TYPE } from '../../constants';
import { networkFromApi } from '../network';
import type { Sim, SimApiResponse, SimToApiInput, SimUpdatePayload } from './types';

export * from './types';

export function toApi(
  sim: SimToApiInput,
  isOrgMember: boolean,
  hasFeature: (feature: string) => boolean,
): SimUpdatePayload {
  return {
    id: sim.id,
    label: sim.label || null,
    activated: sim.activated,
    is_softsim: sim.isSoftSim || false,
    connector: sim.connector || null,
    network_whitelist: sim.networkWhitelist || null,
    imei_lock: sim.imeiLock || null,
    price_plan_alias: sim.pricePlan || null,
    // Because of the way useForm works, we can either get the tags with their IDs set to `id` or `value`.
    // The issue is that Tags use `id` and `name`, and ComboBox use `value` and `label`.
    tags: isOrgMember ? undefined : sim.tags.map(t => t.id || t.value),
    data_limit:
      sim.dataLimitTotal && sim.dataLimitType && (hasFeature('sims.dataLimit') || hasFeature('management.webhooks'))
        ? {
            total: hasFeature('sims.dataLimit') ? sim.dataLimitTotal : undefined,
            type: hasFeature('sims.dataLimit') ? sim.dataLimitType : undefined,
            alert_threshold: hasFeature('management.webhooks') ? sim.dataLimitAlertThreshold || null : undefined,
          }
        : undefined,
    technologies: sim.technologies,
    tech_fee: sim.techFee || null,
    cost_alert: {
      period: sim.costAlertPeriod ?? null,
      threshold: sim.costAlertThreshold ?? null,
    },
  };
}

export function fromApi(sim: SimApiResponse, isOrgMember: boolean): Sim {
  const tags = sim.tags
    ? sim.tags
        .map(t => ({
          name: t.name,
          id: t.id,
          canWrite: t.can_write,
          color: t.color,
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    : [];
  return {
    id: sim.id,
    label: sim.label || '',
    activated: sim.activated,
    connector: sim.connector || '',
    iccid: sim.iccid,
    imeiLock: sim.imei_lock || '',
    imei: sim.imei || '',
    imsi: sim.imsi || '',
    type: sim.type === SOFTSIM_TYPE ? 'SoftSIM' : sim.type === ESIM_TYPE ? 'eSIM' : 'Physical',
    msisdn: sim.msisdn || '',
    deviceInfo: sim.device_info,
    ipv4: sim.ipv4,
    networkWhitelist: sim.network_whitelist || '',
    usage: sim.usage,
    pricePlan: sim.price_plan_alias || '',
    online: sim.online,
    onlineAt: sim.online_at,
    lastCameOnlineAt: sim.last_came_online_at,
    network: networkFromApi(sim.network),
    location: sim.location
      ? {
          lat: sim.location.lat,
          lng: sim.location.lng,
          cellId: sim.location.cell_id,
          locationAreaCode: sim.location.location_area_code,
          accuracy: sim.location.accuracy,
        }
      : {},
    tags,
    dataLimit: sim.data_limit
      ? {
          used: sim.data_limit.used,
          total: sim.data_limit.total,
          type: sim.data_limit.type,
          period: sim.data_limit.period,
          resetsAt: sim.data_limit.resets_at,
          alertThreshold: sim.data_limit.alert_threshold,
        }
      : {},
    dataLimitType: sim?.data_limit?.type,
    dataLimitTotal: sim?.data_limit?.total,
    dataLimitAlertThreshold: sim?.data_limit?.alert_threshold,
    costAlert: sim.cost_alert
      ? {
          period: sim.cost_alert.period,
          threshold: sim.cost_alert.threshold,
          resetsAt: sim.cost_alert.resets_at,
        }
      : {},
    costAlertPeriod: sim?.cost_alert?.period,
    costAlertThreshold: sim?.cost_alert?.threshold,
    canEdit: isOrgMember ? !!tags.find(t => t.canWrite) : true,
    technologies: sim.technologies,
    checked: false,
    techFee: sim.tech_fee || '',
    apn: sim.apn_profile,
    activationCode: sim.activation_code || null,
  };
}
